import React, { useEffect, useRef, MutableRefObject } from 'react'
import { useTranslation } from 'react-i18next'

import Modal from '../Modal'
import { sendAction } from '../../api/api'
import { actionType } from '../../constants/messageActionType'
import { IframeSettingsType } from '../../models/IframeSettingsType'
import { wrapTextAddLinks } from '../../helper/wrapTextAddLinks'
import { openNewTab } from '../../helper/openNewTab'
import { focusTrapCreator } from '../../helper/focusTrapCreator'
import { keyType } from '../../constants/string'
import { sendUserEventToParent } from '../../actions/static'

import {
  CloseWidgetContent,
  CloseWidgetPaper,
  CloseWidgetButtonsContainer,
  CloseWidgetButton,
} from './CloseWidgetModal.style'

interface Props {
  toggleWidget: () => void
  setIsNeedCloseWidget: (value: boolean) => void
  setIsCloseWidgetModalOpened: (p: boolean) => void
  setIframe: (p: IframeSettingsType) => void
  botId: number
  chatId: number
  closePopupText: string
  closePopupScheduleGCText: string
  shouldShowClosePopupScheduleGCText: boolean
  keepChattingButtonText: string
  closeChatButtonText: string
  iframeSettings: IframeSettingsType
  isEmpowerPre: boolean
  isEmpowerPost: boolean
  persistentMenuButtonRef: MutableRefObject<HTMLButtonElement>
}

const CloseWidgetModal: React.FC<Props> = ({
  toggleWidget,
  setIsNeedCloseWidget,
  setIsCloseWidgetModalOpened,
  setIframe,
  botId,
  chatId,
  closePopupText,
  closePopupScheduleGCText,
  shouldShowClosePopupScheduleGCText,
  keepChattingButtonText,
  closeChatButtonText,
  iframeSettings,
  isEmpowerPre,
  isEmpowerPost,
  persistentMenuButtonRef
}) => {
  const output = useRef<HTMLDivElement>(null)
  const closeModalRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const isEmpower = isEmpowerPre || isEmpowerPost

  useEffect(() => {
    linkEditorListener()
    closeModalRef.current.focus()
  }, [closeModalRef.current])

  const linkEditorListener = (): void => {
    const node: NodeListOf<HTMLElement> = output.current.querySelectorAll('.linkEdited')
    const links = [...node]
    links.length > 0 &&
    links.forEach(link => {
      const extension = JSON.parse(link.getAttribute('data-extension'))
      const url = link.getAttribute('data-href')
      const event = link.getAttribute('activity-event')
      link.onclick = (e) => {
        e.preventDefault()
        sendUserEventToParent(event)
        if (extension) {
          handleCloseModal()
          setIframe({
            iframeSrc: url,
            isIframeOpen: true,
            questionnaireName: iframeSettings.questionnaireName
          })
        } else {
          openNewTab(url)
        }
      }
      link.onkeydown = (e) => {
        const currentKey = e.key.toLowerCase()
        if (currentKey === keyType.ENTER) {
          e.preventDefault()
          sendUserEventToParent(event)
          if (extension) {
            handleCloseModal()
            setIframe({
              iframeSrc: url,
              isIframeOpen: true,
              questionnaireName: iframeSettings.questionnaireName
            })
          } else {
            openNewTab(url)
          }
        }
      }
    })
  }

  const handleCloseModal = (): void => {
    sendAction(actionType.KEEP_CHATTING, botId, chatId)
    setIsNeedCloseWidget(false)
    setIsCloseWidgetModalOpened(false)
    persistentMenuButtonRef.current.focus()
  }

  const handleCloseWidget = (): void => {
    sendAction(actionType.CLOSE_WIDGET, botId, chatId)
    setIsNeedCloseWidget(false)
    toggleWidget()
    setIsCloseWidgetModalOpened(false)
  }

  const closeWidgetContent = shouldShowClosePopupScheduleGCText
    ? `${closePopupText} <br><br> ${closePopupScheduleGCText}`
    : closePopupText

  return (
    <Modal
      onClose={handleCloseModal}
    >
      <CloseWidgetPaper
        ref={closeModalRef}
        tabIndex={0}
        role='dialog'
        aria-modal='true'
        aria-labelledby='close-widget-modal-title'
        onKeyDown={(e) => focusTrapCreator(e, closeModalRef.current, true)}
      >
        <CloseWidgetContent
          ref={output}
          id='close-widget-modal-title'
          dangerouslySetInnerHTML={{
            __html: wrapTextAddLinks(closeWidgetContent, t('external_link_icon'), isEmpower)
          }}
        />

        <CloseWidgetButtonsContainer>
          <CloseWidgetButton
            data-test='close-chat-btn'
            agreeButton={false}
            onClick={handleCloseWidget}
          >
            {closeChatButtonText}
          </CloseWidgetButton>
          <CloseWidgetButton
            data-test='keep-chatting-btn'
            agreeButton={true}
            onClick={handleCloseModal}
          >
            {keepChattingButtonText}
          </CloseWidgetButton>
        </CloseWidgetButtonsContainer>
      </CloseWidgetPaper>
    </Modal>
  )
}

export default CloseWidgetModal
